import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { MANAGER_SESSION_EXPIRED_EVENT, MANAGER_TOKEN_KEY } from "@/lib/authSession";
import { hasTsSession, TS_SESSION_CHANGED_EVENT } from "@/lib/tsIdentity";

const AuthContext = createContext(null);

const TOKEN_KEY = MANAGER_TOKEN_KEY;

/**
 * A live TS session is Ops's own credential now — it authenticates directly,
 * with no manager-session exchange. A legacy `ttt_auth_token` left over from
 * before that migration (or from the emergency password fallback) must not be
 * trusted once a TS session exists, or an expired TS session could fall back
 * to a stale manager token and loop; drop it here so it cannot.
 */
function computeAuthState() {
  if (hasTsSession()) {
    if (localStorage.getItem(TOKEN_KEY)) {
      localStorage.removeItem(TOKEN_KEY);
    }
    return { mode: "ts", legacyToken: null };
  }
  return { mode: null, legacyToken: localStorage.getItem(TOKEN_KEY) };
}

export function AuthProvider({ children }) {
  const [state, setState] = useState(computeAuthState);

  useEffect(() => {
    // MANAGER_SESSION_EXPIRED_EVENT and TS_SESSION_CHANGED_EVENT both fire in
    // this same tab (a same-tab localStorage write never triggers "storage"),
    // so this is what keeps ManageLogin's post-TsSessionGate navigation and an
    // API 401's cleanup from leaving isAuthenticated stale until a reload.
    const sync = () => setState(computeAuthState());
    const handleStorage = (event) => {
      if (event.key === TOKEN_KEY || event.key == null) sync();
    };

    window.addEventListener(MANAGER_SESSION_EXPIRED_EVENT, sync);
    window.addEventListener(TS_SESSION_CHANGED_EVENT, sync);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(MANAGER_SESSION_EXPIRED_EVENT, sync);
      window.removeEventListener(TS_SESSION_CHANGED_EVENT, sync);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const login = useCallback((newToken) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setState(computeAuthState());
  }, []);

  /** Drops the legacy manager token only — never the TS session behind it. */
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setState(computeAuthState());
  }, []);

  const value = useMemo(
    () => ({
      token: state.legacyToken,
      isAuthenticated: state.mode === "ts" || !!state.legacyToken,
      login,
      logout,
    }),
    [state, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
